/**
 * Workspace chat encryption.
 *
 * Messages are encrypted under a key derived from the **workspace root's node
 * key** — the same key that opens the workspace's files. That is the whole
 * design, and it has a property worth stating plainly: the set of people who
 * can read the messages is exactly the set who can read the files, by
 * construction rather than by a permission check. Removing someone from a
 * workspace deletes their wrapped copy of that one key and closes both at once.
 *
 * Derived rather than reused directly, following `thumbnails.ts`. The two live
 * on different paths with different caching and different bugs available to
 * them, and a mistake in the chat path should not become a way to read a file.
 * HKDF with a distinct `info` label makes them computationally unrelated.
 *
 * The AAD stops a ciphertext being *moved*: a message body spliced into a
 * filename column, or a thumbnail into a message, fails to decrypt rather than
 * quietly succeeding.
 */

import {
  aesDecryptString,
  aesEncryptString,
  exportRaw,
  hkdfAesKey,
  utf8
} from './primitives';

/** Domain separator. Part of the format — changing it makes every existing
 *  message unreadable. */
const CHAT_INFO = 'mauvely.cloud.workspace.chat.v1';
const CHAT_AAD = utf8(CHAT_INFO);

/**
 * The same construction for a *project* channel, off the project's own node key.
 *
 * A distinct `info` label rather than reusing the workspace one: the two keys
 * must be computationally unrelated, so that holding a project's chat key tells
 * you nothing about the workspace's, and vice versa. Since a project's node key
 * is not derivable from the workspace root's either (migration 0007), the
 * property this inherits is the one that matters — the set of people who can
 * read a project's messages is exactly the set who can read its files, by
 * construction rather than by a permission check.
 */
const PROJECT_CHAT_INFO = 'mauvely.cloud.project.chat.v1';
const PROJECT_CHAT_AAD = utf8(PROJECT_CHAT_INFO);

/** A third AAD, so a message cannot be moved between a channel and the
 *  workspace-wide conversation by rewriting one column. */
const CHANNEL_CHAT_AAD = utf8('mauvely.cloud.channel.chat.v1');

/**
 * The chat key for a workspace, from its root node key.
 *
 * Node keys are extractable by design (see `unwrapNodeKey`) because they get
 * re-wrapped whenever a node is moved or shared, so the raw bytes are available
 * here as HKDF input. The derived key is not extractable.
 */
export const chatKey = async (workspaceKey: CryptoKey): Promise<CryptoKey> =>
  hkdfAesKey(await exportRaw(workspaceKey), CHAT_INFO);

/**
 * The chat key for a named channel.
 *
 * Derived from the **workspace** key, not from anything channel-specific, with
 * the channel id in the label. So every member of the workspace can read every
 * channel — a channel organises a conversation, it does not partition one. A
 * conversation that needs its own membership is a project, which already has its
 * own key wrapped per member.
 */
export const namedChannelChatKey = async (
  workspaceKey: CryptoKey,
  channelId: string
): Promise<CryptoKey> =>
  hkdfAesKey(await exportRaw(workspaceKey), `mauvely.cloud.channel.chat.v1:${channelId}`);

/** The chat key for a project, from the project root's node key. */
export const projectChatKey = async (projectKey: CryptoKey): Promise<CryptoKey> =>
  hkdfAesKey(await exportRaw(projectKey), PROJECT_CHAT_INFO);

/**
 * Encrypt a message body. Returns the same `<iv>.<ct>` text every other
 * encrypted value in the product uses, so one decrypt path covers it.
 *
 * `channel` picks the AAD. It must match the key: a project message sealed with
 * the workspace AAD would decrypt to nothing in the project channel and vice
 * versa — which is the point, since it also stops a message being moved between
 * channels by rewriting one column.
 */
export type ChannelKind = 'workspace' | 'project' | 'channel';

const aadFor = (kind: ChannelKind) =>
  kind === 'project' ? PROJECT_CHAT_AAD : kind === 'channel' ? CHANNEL_CHAT_AAD : CHAT_AAD;

export const encryptMessage = (
  key: CryptoKey,
  text: string,
  channel: ChannelKind = 'workspace'
): Promise<string> => aesEncryptString(key, text, aadFor(channel));

export const decryptMessage = (
  key: CryptoKey,
  payload: string,
  channel: ChannelKind = 'workspace'
): Promise<string> => aesDecryptString(key, payload, aadFor(channel));

/**
 * The `@name` spans in a message, resolved to user ids.
 *
 * Mentions are sent to the server as **plaintext** user ids, separately from
 * the encrypted body, because an unread badge and a "you were mentioned" pill
 * have to work without decrypting anything. That is a real disclosure — the
 * server learns who was tagged and when — and it is recorded in
 * docs/SECURITY.md rather than left implicit.
 *
 * Matching is done here, against the member list the client already holds, so
 * the server is never asked to interpret the text.
 *
 * Longest token first, and **matched spans are consumed**. Sorting alone is not
 * enough: "@Sam" is a substring of "@Samira", so a plain scan tags Sam as well
 * every time somebody addresses Samira. Blanking the span once it is claimed —
 * with spaces, so every later offset still lines up — is what stops one
 * person's name matching inside another's.
 */
export function findMentions(
  text: string,
  members: { userId: string | null; name: string; email: string }[]
): string[] {
  const found = new Set<string>();

  const candidates = members
    // A pending invite has no id to tag: the person has no account yet, so
    // there is nothing the server could resolve a mention to.
    .filter((m): m is typeof m & { userId: string } => !!m.userId)
    .flatMap((m) => [
      { userId: m.userId, token: m.name },
      { userId: m.userId, token: m.email }
    ])
    .filter((c) => c.token)
    .sort((a, b) => b.token.length - a.token.length);

  let haystack = text.toLowerCase();
  for (const { userId, token } of candidates) {
    const needle = `@${token.toLowerCase()}`;
    if (!haystack.includes(needle)) continue;

    found.add(userId);
    haystack = haystack.split(needle).join(' '.repeat(needle.length));
  }

  return [...found];
}

/** The wire form of a mention inside a message body: `<@user-id>`. */
const MENTION_TOKEN = /<@([A-Za-z0-9_-]{1,64})>/g;

/**
 * Rewrite `@name` spans into `<@user-id>` tokens, and report the ids found.
 *
 * The body is stored **encrypted**, so this token is not a disclosure — it is
 * only what the recipient's client reads back to draw the mention as a chip
 * rather than as the literal characters the author typed. Before this, a mention
 * survived the round trip as prose: `findMentions` resolved the ids correctly and
 * the notification fired, but the message itself rendered `@Samira Khan` as text,
 * so a tag looked exactly like typing someone's name.
 *
 * Resolving at send time rather than at render time is what makes it stable. The
 * id is fixed; the display name is not, and a message that says `<@u_7f3…>` still
 * renders correctly after someone changes their name, where stored prose would
 * quietly stop matching anybody.
 *
 * The same consume-as-you-go rule as `findMentions` applies, for the same reason
 * ("@Sam" inside "@Samira"), so the two agree on every input.
 */
export function encodeMentions(
  text: string,
  members: { userId: string | null; name: string; email: string }[]
): { body: string; mentions: string[] } {
  const candidates = members
    .filter((m): m is typeof m & { userId: string } => !!m.userId)
    .flatMap((m) => [
      { userId: m.userId, token: m.name },
      { userId: m.userId, token: m.email }
    ])
    .filter((c) => c.token)
    .sort((a, b) => b.token.length - a.token.length);

  const found = new Set<string>();
  // Worked on as a character array so a replacement never shifts the offsets of
  // a span that has not been examined yet — the whole point of the "blank it
  // out" trick in findMentions, done here without losing the original text.
  let body = text;
  const claimed: boolean[] = new Array(text.length).fill(false);

  for (const { userId, token } of candidates) {
    const needle = `@${token}`.toLowerCase();
    const hay = body.toLowerCase();
    let at = hay.indexOf(needle);
    while (at !== -1) {
      const free = claimed.slice(at, at + needle.length).every((c) => !c);
      if (free) {
        found.add(userId);
        const replacement = `<@${userId}>`;
        body = body.slice(0, at) + replacement + body.slice(at + needle.length);
        claimed.splice(at, needle.length, ...new Array(replacement.length).fill(true));
        at = body.toLowerCase().indexOf(needle, at + replacement.length);
      } else {
        at = hay.indexOf(needle, at + 1);
      }
    }
  }

  return { body, mentions: [...found] };
}

export type MessagePart =
  | { kind: 'text'; text: string }
  | { kind: 'mention'; userId: string };

/**
 * Split a decrypted body into text and mention chips for rendering.
 *
 * Messages written before `encodeMentions` existed carry no tokens, so they fall
 * back to matching member names in the text — otherwise every message already in
 * a workspace would keep rendering its tags as prose forever.
 */
export function parseMessageBody(
  body: string,
  members: { userId: string | null; name: string; email: string }[] = []
): MessagePart[] {
  const parts: MessagePart[] = [];
  let last = 0;

  MENTION_TOKEN.lastIndex = 0;
  for (let m = MENTION_TOKEN.exec(body); m; m = MENTION_TOKEN.exec(body)) {
    if (m.index > last) parts.push({ kind: 'text', text: body.slice(last, m.index) });
    parts.push({ kind: 'mention', userId: m[1]! });
    last = m.index + m[0].length;
  }
  if (last < body.length) parts.push({ kind: 'text', text: body.slice(last) });

  // No tokens at all: an older message. Re-derive the spans from the member list.
  if (!parts.some((p) => p.kind === 'mention') && members.length) {
    const encoded = encodeMentions(body, members);
    if (encoded.mentions.length) return parseMessageBody(encoded.body);
  }

  return parts.length ? parts : [{ kind: 'text', text: body }];
}
